import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Icon,
  Square,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { RiDownload2Line, RiLink } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Layout from "../../../components/student/Layout/layout";
import AddVoucher from "../../../containers/student/models/add-voucher";
import VideoPreview from "../../../containers/student/video/videoPreview";
import axiosInstance from "../../../helpers/api";
import { buildStudentVideoUrl } from "../../../helpers/studentVideoLink";
import { getVideosByChapterIdRequest } from "../../../modules/students-modules/videos/Actions";

const StudentVideo = () => {
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [playBack, setPlayBack] = useState("");
  const [initialLoad, setInitialLoad] = useState(true);
  const baseUrl = "https://asquera.com/asquera";
  const [getSearchParams] = useSearchParams();
  const params = useParams();
  const video_id = getSearchParams.get("video") || params.id;
  const chapter_id = getSearchParams.get("chapter");
  const chapter_name = getSearchParams.get("name");
  const section_id = getSearchParams.get("section");
  const section_name = getSearchParams.get("sectionName");
  const dispatch = useDispatch();

  const chapterVideos = useSelector(
    (state) => state.chapterVideos.chapterVideos
  );
  const reduxLoading = useSelector((state) => state.chapterVideos.isLoading);
  const isLoading = initialLoad || reduxLoading;

  const selectedData = useMemo(
    () =>
      chapterVideos?.find((data) => String(data?.id) === String(video_id)) ||
      {},
    [chapterVideos, video_id]
  );

  const getVideoData = async (id) => {
    const res = await axiosInstance.get(`/students/videos/otp/${id}`);
    await setOtp(res?.data?.otp);
    await setPlayBack(res?.data?.playbackInfo);
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 2000);
  };

  useEffect(() => {
    setInitialLoad(true);
    if (chapter_id) {
      dispatch(getVideosByChapterIdRequest(chapter_id));
    } else {
      setInitialLoad(false);
    }
  }, [chapter_id, dispatch]);

  useEffect(() => {
    if (reduxLoading) {
      setInitialLoad(false);
    }
  }, [reduxLoading]);

  useEffect(() => {
    if (selectedData?.id && selectedData?.video_type == 1) {
      getVideoData(selectedData.id);
    }
  }, [selectedData?.id, selectedData?.video_type]);

  const chaptersListPath = (() => {
    if (!section_id) return "/videos/sections";
    const params = new URLSearchParams();
    if (section_name) params.set("name", section_name);
    if (chapter_id) params.set("chapter", String(chapter_id));
    const query = params.toString();
    return `/videos/chapters/${section_id}${query ? `?${query}` : ""}`;
  })();

  const handleCopyLink = async () => {
    if (!chapter_id || !video_id) {
      toast.error("This video link is missing chapter information");
      return;
    }
    try {
      await navigator.clipboard.writeText(
        buildStudentVideoUrl({
          videoId: video_id,
          chapterId: chapter_id,
          chapterName: chapter_name,
          sectionId: section_id,
          sectionName: section_name,
        })
      );
      toast.success("Video link copied");
    } catch {
      toast.error("Failed to copy video link");
    }
  };

  return (
    <Layout color="main_1" bg="white" isLoading={isLoading}>
      <Container
        maxW="container.2xl"
        px={{ base: 2, md: 4 }}
        p={{ base: 2, md: 4, lg: 6, sm: 6 }}
      >
        <VStack spacing={6} w="100%" alignItems="flex-start" mt="20px">
          <Flex
            w="100%"
            justify="space-between"
            align="center"
            gap={3}
            flexWrap="wrap"
          >
            <Breadcrumb fontSize="sm">
              <BreadcrumbItem>
                <Link to="/videos/sections">
                  <Text>Video</Text>
                </Link>
              </BreadcrumbItem>
              {section_id && (
                <BreadcrumbItem>
                  <Link to={chaptersListPath}>
                    <Text>Chapters</Text>
                  </Link>
                </BreadcrumbItem>
              )}
              <BreadcrumbItem isCurrentPage>
                <Text textTransform="capitalize">{chapter_name}</Text>
              </BreadcrumbItem>
            </Breadcrumb>
            {chapter_id && video_id && (
              <Button
                size="sm"
                leftIcon={<RiLink />}
                variant="outline"
                borderColor="main_1"
                color="main_1"
                onClick={handleCopyLink}
              >
                Copy link
              </Button>
            )}
          </Flex>

          {!chapter_id ? (
            <Heading
              as="h6"
              color="main_1"
              textAlign="center"
              w="100%"
              fontSize="lg"
            >
              This video link is missing chapter information. Please open the
              video from the Videos tab.
            </Heading>
          ) : chapterVideos?.length === 0 ? (
            <Heading
              as="h6"
              color="main_1"
              textAlign="center"
              w="100%"
              fontSize="lg"
            >
              You Didn't pay for this chapter
              <a
                href="https://wa.me/+201025830160"
                style={{ textDecoration: "underline" }}
                target="_blank"
                rel="noreferrer"
              >
                , Connect with the assistant!
              </a>
            </Heading>
          ) : !selectedData?.id ? (
            <Heading
              as="h6"
              color="main_1"
              textAlign="center"
              w="100%"
              fontSize="lg"
            >
              This video is not available in this chapter.
            </Heading>
          ) : selectedData?.url == "no" ? (
            <Heading>Coming Soon</Heading>
          ) : (
            <Box w="100%">
              <VStack spacing="6" alignItems="flex-start" w="100%">
                <VideoPreview
                  data={selectedData}
                  loading={loading}
                  otp={otp}
                  playBack={playBack}
                />
                {selectedData?.files?.length > 0 && (
                  <VStack
                    w="100%"
                    alignItems="flex-start"
                    color="main_1"
                    spacing="4"
                  >
                    <Text
                      fontSize={{ base: "lg", md: "xl" }}
                      fontWeight="500"
                    >
                      Video materials
                    </Text>
                    <Flex w="100%" flexWrap="wrap" gap="3">
                      {selectedData?.files?.map((val, idx) => (
                        <HStack
                          key={idx}
                          w={{ base: "100%", sm: "48%", md: "31.5%" }}
                          h="60px"
                          bg="gray.50"
                          justify="space-between"
                          borderRadius="md"
                          p="3"
                          position="relative"
                          transition="all 0.2s"
                          _hover={{ bg: "gray.100" }}
                        >
                          <Text fontSize="sm">File title</Text>
                          <Square
                            size="32px"
                            bg="main_1"
                            color="white"
                            borderRadius="md"
                          >
                            <Icon as={RiDownload2Line} />
                          </Square>
                          <a
                            href={`${baseUrl}/${val?.file}`}
                            download
                            style={{
                              width: "100%",
                              height: "100%",
                              position: "absolute",
                            }}
                            target="_blank"
                            rel="noreferrer"
                          />
                        </HStack>
                      ))}
                    </Flex>
                  </VStack>
                )}
              </VStack>
            </Box>
          )}
        </VStack>
      </Container>
      <AddVoucher video_id={selectedData?.id} />
    </Layout>
  );
};

export default StudentVideo;
